import type { OldNutrientJson } from '@shared/app/types/OldNutrientJson';
import { isNutrientCalculable } from './isNutrientCalculable';

/**
 * 全栄養素キー（kiwi-user 表示用 15 種 + 編集画面の栄養価計算用 30 種の和集合 31 種）
 * NUTRIENT_DECIMAL_PLACES / NUTRIENT_UNITS / NUTRIENT_LABELS はこのキーセットを基準に整備する
 */
export const NUTRIENT_KEYS = [
  'energy',
  'protein',
  'lipid',
  'carbohydrate',
  'mineral_sodium',
  'mineral_potassium',
  'mineral_calcium',
  'mineral_magnesium',
  'mineral_phosphorus',
  'mineral_iron',
  'mineral_zinc',
  'mineral_copper',
  'vitamin_a_beta_carotene_equiv',
  'vitamin_a_retinai_activity_equiv',
  'vitamin_d',
  'vitamin_e_tocopherols_alpha',
  'vitamin_k',
  'vitamin_thiamin',
  'vitamin_riboflavin',
  'vitamin_niacin',
  'vitamin_vitamin_b6',
  'vitamin_vitamin_b12',
  'vitamin_folate',
  'vitamin_pantothenic_acid',
  'vitamin_ascorbic_acid',
  'cholesterol',
  'fatty_acids_saturated',
  'dietary_fibers_soluble',
  'dietary_fibers_insoluble',
  'dietary_fibers_total',
  'salt_equiv',
] as const;

export type NutrientKey = (typeof NUTRIENT_KEYS)[number];

/**
 * kiwi-user 公開ページ表示用の栄養素キー（主要 15 種）
 * RecipeNutrients / NutrientFoodComposition / calcRecipeNutrients のデフォルト計算対象。
 * 並び・要素は従来の NUTRIENT_KEYS と完全一致させ、後方互換を保つ。
 */
export const MAIN_NUTRIENT_KEYS = [
  'energy',
  'protein',
  'lipid',
  'carbohydrate',
  'mineral_calcium',
  'mineral_iron',
  'vitamin_thiamin',
  'vitamin_riboflavin',
  'vitamin_folate',
  'vitamin_ascorbic_acid',
  'cholesterol',
  'dietary_fibers_total',
  'salt_equiv',
  'fatty_acids_saturated',
  'mineral_sodium',
] as const satisfies readonly NutrientKey[];

export type MainNutrientKey = (typeof MAIN_NUTRIENT_KEYS)[number];

/**
 * kiwi-admin レシピ編集画面の栄養価計算用の栄養素キー（30 種）
 * 栄養価計算はこの並び・集合を対象とする。
 */
export const PROSPECT_NUTRIENT_KEYS = [
  'energy',
  'protein',
  'lipid',
  'carbohydrate',
  'mineral_sodium',
  'mineral_potassium',
  'mineral_calcium',
  'mineral_magnesium',
  'mineral_phosphorus',
  'mineral_iron',
  'mineral_zinc',
  'mineral_copper',
  'vitamin_a_beta_carotene_equiv',
  'vitamin_a_retinai_activity_equiv',
  'vitamin_d',
  'vitamin_e_tocopherols_alpha',
  'vitamin_k',
  'vitamin_thiamin',
  'vitamin_riboflavin',
  'vitamin_niacin',
  'vitamin_vitamin_b6',
  'vitamin_vitamin_b12',
  'vitamin_folate',
  'vitamin_pantothenic_acid',
  'vitamin_ascorbic_acid',
  'cholesterol',
  'dietary_fibers_soluble',
  'dietary_fibers_insoluble',
  'dietary_fibers_total',
  'salt_equiv',
] as const satisfies readonly NutrientKey[];

export type ProspectNutrientKey = (typeof PROSPECT_NUTRIENT_KEYS)[number];

/**
 * 栄養素ごとの小数点桁数
 * 日本食品標準成分表の表示規格に準拠
 */
export const NUTRIENT_DECIMAL_PLACES: Record<NutrientKey, number> = {
  energy: 0,
  protein: 1,
  lipid: 1,
  carbohydrate: 1,
  mineral_sodium: 0,
  mineral_potassium: 0,
  mineral_calcium: 0,
  mineral_magnesium: 0,
  mineral_phosphorus: 0,
  mineral_iron: 1,
  mineral_zinc: 1,
  mineral_copper: 2,
  vitamin_a_beta_carotene_equiv: 0,
  vitamin_a_retinai_activity_equiv: 0,
  vitamin_d: 1,
  vitamin_e_tocopherols_alpha: 1,
  vitamin_k: 0,
  vitamin_thiamin: 2,
  vitamin_riboflavin: 2,
  vitamin_niacin: 1,
  vitamin_vitamin_b6: 2,
  vitamin_vitamin_b12: 1,
  vitamin_folate: 0,
  vitamin_pantothenic_acid: 2,
  vitamin_ascorbic_acid: 0,
  cholesterol: 0,
  fatty_acids_saturated: 0,
  dietary_fibers_soluble: 1,
  dietary_fibers_insoluble: 1,
  dietary_fibers_total: 1,
  salt_equiv: 1,
};

/**
 * 栄養素の単位
 */
export const NUTRIENT_UNITS: Record<NutrientKey, string> = {
  energy: 'kcal',
  protein: 'g',
  lipid: 'g',
  carbohydrate: 'g',
  mineral_sodium: 'mg',
  mineral_potassium: 'mg',
  mineral_calcium: 'mg',
  mineral_magnesium: 'mg',
  mineral_phosphorus: 'mg',
  mineral_iron: 'mg',
  mineral_zinc: 'mg',
  mineral_copper: 'mg',
  vitamin_a_beta_carotene_equiv: 'μg',
  vitamin_a_retinai_activity_equiv: 'μg',
  vitamin_d: 'μg',
  vitamin_e_tocopherols_alpha: 'mg',
  vitamin_k: 'μg',
  vitamin_thiamin: 'mg',
  vitamin_riboflavin: 'mg',
  vitamin_niacin: 'mg',
  vitamin_vitamin_b6: 'mg',
  vitamin_vitamin_b12: 'μg',
  vitamin_folate: 'μg',
  vitamin_pantothenic_acid: 'mg',
  vitamin_ascorbic_acid: 'mg',
  cholesterol: 'mg',
  fatty_acids_saturated: 'g',
  dietary_fibers_soluble: 'g',
  dietary_fibers_insoluble: 'g',
  dietary_fibers_total: 'g',
  salt_equiv: 'g',
};

/**
 * 栄養素のラベル（日本語表示用）
 */
export const NUTRIENT_LABELS: Record<NutrientKey, string> = {
  energy: 'エネルギー',
  protein: 'たんぱく質',
  lipid: '脂質',
  carbohydrate: '炭水化物',
  mineral_sodium: 'ナトリウム',
  mineral_potassium: 'カリウム',
  mineral_calcium: 'カルシウム',
  mineral_magnesium: 'マグネシウム',
  mineral_phosphorus: 'リン',
  mineral_iron: '鉄',
  mineral_zinc: '亜鉛',
  mineral_copper: '銅',
  vitamin_a_beta_carotene_equiv: 'β-カロテン当量',
  vitamin_a_retinai_activity_equiv: 'レチノール活性当量',
  vitamin_d: 'ビタミンD',
  vitamin_e_tocopherols_alpha: 'α-トコフェロール',
  vitamin_k: 'ビタミンK',
  vitamin_thiamin: 'ビタミンB1',
  vitamin_riboflavin: 'ビタミンB2',
  vitamin_niacin: 'ナイアシン',
  vitamin_vitamin_b6: 'ビタミンB6',
  vitamin_vitamin_b12: 'ビタミンB12',
  vitamin_folate: '葉酸',
  vitamin_pantothenic_acid: 'パントテン酸',
  vitamin_ascorbic_acid: 'ビタミンC',
  cholesterol: 'コレステロール',
  fatty_acids_saturated: '飽和脂肪酸',
  dietary_fibers_soluble: '水溶性食物繊維',
  dietary_fibers_insoluble: '不溶性食物繊維',
  dietary_fibers_total: '食物繊維総量',
  salt_equiv: '食塩相当量',
};

/**
 * 栄養素の値を丸める
 */
export function roundNutrient(key: NutrientKey, value: number): number {
  const decimalPlaces = NUTRIENT_DECIMAL_PLACES[key];
  const factor = Math.pow(10, decimalPlaces);
  return Math.round(value * factor) / factor;
}

/**
 * 数値を表示用文字列にフォーマットする
 * 整数値のときは `"x.0"`、それ以外は trailing zero を落とした素の文字列を返す
 */
export function formatFloat(value: number): string {
  return Number.isInteger(value) ? `${value}.0` : String(value);
}

/**
 * 栄養素の値を表示用文字列にフォーマットする
 * 桁数 0 のキーは整数表記、桁数 1 以上のキーは整数値でも `.0` を残す
 */
export function formatNutrient(key: NutrientKey, value: number): string {
  const decimals = NUTRIENT_DECIMAL_PLACES[key];
  const rounded = roundNutrient(key, value);
  if (decimals === 0) return String(rounded);
  return formatFloat(rounded);
}

/**
 * 任意のキーセットで初期値 0 のオブジェクトを生成する内部ヘルパ
 */
function createEmptyNutrientsFor<K extends NutrientKey>(
  keys: readonly K[],
): Record<K, number> {
  const result = {} as Record<K, number>;
  for (const key of keys) {
    result[key] = 0;
  }
  return result;
}

/**
 * 各栄養素の値を格納する型（kiwi-user の主要 15 種）
 */
export type RecipeNutrients = {
  [K in MainNutrientKey]: number;
};

/**
 * 空の栄養素オブジェクトを作成
 */
export function createEmptyNutrients(): RecipeNutrients {
  return createEmptyNutrientsFor(MAIN_NUTRIENT_KEYS) as RecipeNutrients;
}

/**
 * 栄養素計算に必要な食品成分の型（kiwi-user 既存呼び出しと互換の主要 15 種）
 */
export type NutrientFoodComposition = {
  [K in MainNutrientKey]: number;
};

/**
 * 栄養素計算に必要なレシピ材料の型（kiwi-user 既存呼び出しと互換の主要 15 種）
 */
export type RecipeCompositionsForNutrients = Array<{
  weight: number;
  food_composition?: NutrientFoodComposition | null;
}>;

/**
 * 旧データ（old_nutrient_json）から指定キーセットで栄養素を取得
 */
function getNutrientsFromOldNutrientJson<K extends NutrientKey>(
  oldNutrientJson: string | null | undefined,
  keys: readonly K[],
): Record<K, number> {
  const result = createEmptyNutrientsFor(keys);

  if (!oldNutrientJson) {
    return result;
  }

  try {
    const parsed = JSON.parse(oldNutrientJson) as Partial<OldNutrientJson> &
      Record<string, unknown>;
    for (const key of keys) {
      const value = parsed[key];
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        result[key] = numValue;
      }
    }
    return result;
  } catch (e) {
    console.error('Failed to parse old_nutrient_json:', e);
    return result;
  }
}

/**
 * レシピの栄養素を計算する（kiwi-user 既存呼び出し向け、主要 15 種を返す）
 */
export function calcRecipeNutrients(
  recipeCompositions: RecipeCompositionsForNutrients,
  recipeQuantity: number,
  oldNutrientJson: string | null | undefined,
): RecipeNutrients;
/**
 * レシピの栄養素を計算する（任意のキーセット指定。kiwi-admin 編集画面の栄養価計算で利用）
 */
export function calcRecipeNutrients<K extends NutrientKey>(
  recipeCompositions: Array<{
    weight: number;
    food_composition?: Record<K, number> | null | undefined;
  }>,
  recipeQuantity: number,
  oldNutrientJson: string | null | undefined,
  only: readonly K[],
): Record<K, number>;
export function calcRecipeNutrients(
  recipeCompositions: Array<{
    weight: number;
    food_composition?: Partial<Record<NutrientKey, number>> | null | undefined;
  }>,
  recipeQuantity: number,
  oldNutrientJson: string | null | undefined,
  only?: readonly NutrientKey[],
): Record<NutrientKey, number> {
  const keys: readonly NutrientKey[] = only ?? MAIN_NUTRIENT_KEYS;

  if (isNutrientCalculable(recipeCompositions)) {
    const result = createEmptyNutrientsFor(keys);

    for (const composition of recipeCompositions) {
      if (!composition.food_composition) {
        continue;
      }

      // NOTE: FoodCompositionのカラム値 * (weight / quantity) / 100
      // 栄養素の値は100g単位のものが入っているので、100で割る必要がある
      // weight / quantity は1人あたりの材料の重量（g）を表す
      const factor = composition.weight / recipeQuantity / 100.0;

      for (const key of keys) {
        const value = composition.food_composition[key];
        if (value != null) {
          result[key] += value * factor;
        }
      }
    }

    // NOTE: 栄養素ごとに値を丸める
    for (const key of keys) {
      result[key] = roundNutrient(key, result[key]);
    }

    return result as Record<NutrientKey, number>;
  }

  // NOTE: 旧データの場合（weightもしくはfood_compositionが紐付かない）
  return getNutrientsFromOldNutrientJson(oldNutrientJson, keys) as Record<
    NutrientKey,
    number
  >;
}
